import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../components/Layout/MainLayout";
import ConsumptionChart from "../components/Charts/ConsumptionChart";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { FileText, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, X, Building2, ShieldAlert } from "lucide-react";
import NoticeBoard from "../components/NoticeBoard";
import { supabase } from "@/integrations/supabase/client";
import { formatTSh } from "@/lib/currency";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { usePricingRate } from "@/hooks/usePricing";
import { getUserApartmentId } from "@/lib/apartment";
import { useActingAsAdmin } from "@/hooks/useActingAsAdmin";
import { useTenantView } from "@/contexts/TenantViewContext";

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const actingAsAdmin = useActingAsAdmin();
  const { viewingApartmentId } = useTenantView();
  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  const [balanceKwhFromMeter, setBalanceKwhFromMeter] = useState(0);
  const [lastBillAmount, setLastBillAmount] = useState(0);
  const [lastBillMonth, setLastBillMonth] = useState<string>("");
  // Consumption data for all periods
  const [consumptionByPeriod, setConsumptionByPeriod] = useState<{
    day: Array<{ date: string; kwh: number; cost: number }>;
    week: Array<{ date: string; kwh: number; cost: number }>;
    month: Array<{ date: string; kwh: number; cost: number }>;
    year: Array<{ date: string; kwh: number; cost: number }>;
  }>({ day: [], week: [], month: [], year: [] });
  const [chartPeriod, setChartPeriod] = useState<'day' | 'week' | 'month' | 'year'>('month');
  const [noticesExpanded, setNoticesExpanded] = useState(true);
  const [userName, setUserName] = useState("");
  const [lastTopUp, setLastTopUp] = useState<string | null>(null);
  const [rechargeNotification, setRechargeNotification] = useState<{
    amount: number;
    kwh: number;
    timestamp: string;
  } | null>(null);
  const [apartmentInfo, setApartmentInfo] = useState<{ number: string; building: string } | null>(null);
  const [overdraftInfo, setOverdraftInfo] = useState<{ overdrawn: number; limit: number } | null>(null);

  // Get dynamic pricing rate from user's building (no fallback)
  const ratePerKwh = usePricingRate(0);

  // Use meter balance (kWh) directly, fall back to calculated from TZS balance
  const balanceKwh = balanceKwhFromMeter > 0 ? balanceKwhFromMeter : (ratePerKwh > 0 ? balance / ratePerKwh : 0);
  const isLowBalance = balanceKwh < 100;

  const fetchUserData = useCallback(async () => {
    // Reset apartment-scoped state before fetching — prevents stale display when apartment changes
    setOverdraftInfo(null);
    setBalanceKwhFromMeter(0);
    setConsumptionByPeriod({ day: [], week: [], month: [], year: [] });
    try {
      // Check if we have a Supabase session
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const isDemoUser = !session && user?.email === "demo@sme.ng";
      const userId = isDemoUser ? "11111111-2222-3333-4444-555555555555" : user?.id;

      // Fetch user name
      if (isDemoUser) {
        console.log("Dashboard: Fetching demo user data via edge function");
        const response = await fetch(
          `https://sjszhatwjwafioakcncx.supabase.co/functions/v1/get-profile?email=demo@sme.ng`,
          {
            headers: {
              Authorization: `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNqc3poYXR3andhZmlvYWtjbmN4Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTAxMDU5NjAsImV4cCI6MjA2NTY4MTk2MH0.auq6Tek2SaVWjCSt6ew-VQEX21sV0Dx7YJU1vWNTjMA`,
            },
          },
        );

        if (response.ok) {
          const profileData = await response.json();
          const fullName = `${profileData.first_name || ""} ${profileData.last_name || ""}`.trim();
          setUserName(fullName || profileData.email?.split("@")[0] || "User");
        }
      } else if (session) {
        const { data: userData } = await supabase
          .from("users")
          .select("first_name, last_name")
          .eq("id", user?.id)
          .maybeSingle();

        if (userData) {
          const fullName = `${userData.first_name || ""} ${userData.last_name || ""}`.trim();
          setUserName(fullName || user?.email?.split("@")[0] || "User");

          // Use override apartment (owner/concierge viewing a unit) or resolve from user
          const apartmentId = viewingApartmentId ?? await getUserApartmentId(user!.id);

          // Fetch apartment label for display
          if (apartmentId) {
            const { data: aptData } = await supabase
              .from('apartments')
              .select('apartment_number, buildings:building_id(name)')
              .eq('id', apartmentId)
              .maybeSingle();
            if (aptData) {
              setApartmentInfo({
                number: aptData.apartment_number,
                building: (aptData.buildings as any)?.name || '',
              });
            }
          }

          // Fetch real meter readings if user has an apartment
          if (apartmentId) {
            // Find smart meter for this apartment (also fetch overdraft limit)
            const { data: meterData } = await supabase
              .from("smart_meters")
              .select("id, overdraft_limit_kwh")
              .eq("apartment_id", apartmentId)
              .maybeSingle();

            if (meterData) {
              // Fetch latest electricity balance from meter
              const { data: latestReading } = await supabase
                .from("meter_readings")
                .select("electricity_balance_kwh, reading_datetime, overdrawn_energy_kwh")
                .eq("meter_id", meterData.id)
                .not("electricity_balance_kwh", "is", null)
                .gt("electricity_balance_kwh", 0)
                .order("reading_datetime", { ascending: false })
                .limit(1)
                .maybeSingle();

              if (latestReading?.electricity_balance_kwh) {
                const meterBalanceKwh = Number(latestReading.electricity_balance_kwh);
                console.log("Dashboard: Meter balance from readings:", meterBalanceKwh, "kWh");
                setBalanceKwhFromMeter(meterBalanceKwh);
                setBalance(meterBalanceKwh * ratePerKwh);
              }

              // Overdraft: same row as balance query (already has overdrawn_energy_kwh)
              const overdrawn = Number(latestReading?.overdrawn_energy_kwh ?? 0);
              const overdraftLimit = Number(meterData.overdraft_limit_kwh ?? 0);
              if (overdrawn > 0 || overdraftLimit > 0) {
                setOverdraftInfo({ overdrawn, limit: overdraftLimit });
              }

              // Fetch last 90 days of meter readings for consumption chart (same as ConsumptionEvolution)
              const ninetyDaysAgo = new Date();
              ninetyDaysAgo.setTime(ninetyDaysAgo.getTime() - 90 * 24 * 60 * 60 * 1000);

              // Supabase has a project-level max-rows limit (default 1000)
              // Fetch data in batches to get all readings
              const allReadings: Array<{ reading_datetime: string; forward_energy_kwh: number }> = [];
              let offset = 0;
              const batchSize = 1000;

              while (true) {
                const { data: batch } = await supabase
                  .from("meter_readings")
                  .select("reading_datetime, forward_energy_kwh")
                  .eq("meter_id", meterData.id)
                  .gte("reading_datetime", ninetyDaysAgo.toISOString())
                  .order("reading_datetime", { ascending: true })
                  .range(offset, offset + batchSize - 1);

                if (!batch || batch.length === 0) break;
                allReadings.push(...batch);
                if (batch.length < batchSize) break;
                offset += batchSize;
              }

              const meterReadings = allReadings;
              console.log('Dashboard: Raw meter readings count:', meterReadings?.length, 'First:', meterReadings?.[0]?.reading_datetime, 'Last:', meterReadings?.[meterReadings.length - 1]?.reading_datetime);

              if (meterReadings && meterReadings.length > 1) {
                // Calculate consumption from forward_energy_kwh deltas
                const dailyGroups = new Map<string, number>();
                const weeklyGroups = new Map<string, number>();
                const monthlyGroups = new Map<string, number>();
                const yearlyGroups = new Map<string, number>();

                // Calculate deltas between consecutive readings
                for (let i = 1; i < meterReadings.length; i++) {
                  const prevForward = Number(meterReadings[i - 1].forward_energy_kwh || 0);
                  const currForward = Number(meterReadings[i].forward_energy_kwh || 0);
                  const delta = currForward - prevForward;
                  // Skip negative deltas and unreasonably large jumps (>30 kWh = corrupt data)
                  // 30 kWh in ~10 min = 180 kW avg, far exceeds any residential/commercial use
                  if (delta < 0 || delta > 30) continue;
                  const kwh = delta;

                  const date = new Date(meterReadings[i].reading_datetime);

                  // Daily: YYYY-MM-DD
                  const dayKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                  dailyGroups.set(dayKey, (dailyGroups.get(dayKey) || 0) + kwh);

                  // Weekly: Use week start date (Monday) as YYYY-MM-DD
                  const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
                  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Days to subtract to get Monday
                  const weekStart = new Date(date);
                  weekStart.setDate(date.getDate() + mondayOffset);
                  const weekKey = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`;
                  weeklyGroups.set(weekKey, (weeklyGroups.get(weekKey) || 0) + kwh);

                  // Monthly: YYYY-MM
                  const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                  monthlyGroups.set(monthKey, (monthlyGroups.get(monthKey) || 0) + kwh);

                  // Yearly: YYYY
                  const yearKey = `${date.getFullYear()}`;
                  yearlyGroups.set(yearKey, (yearlyGroups.get(yearKey) || 0) + kwh);
                }

                // Convert maps to sorted arrays
                const toDataArray = (groups: Map<string, number>) =>
                  Array.from(groups.entries())
                    .sort((a, b) => a[0].localeCompare(b[0]))
                    .map(([date, kwh]) => ({
                      date,
                      kwh: Math.round(kwh * 100) / 100,
                      cost: Math.round(kwh * ratePerKwh * 100) / 100
                    }));

                setConsumptionByPeriod({
                  day: toDataArray(dailyGroups).slice(-30), // Last 30 days
                  week: toDataArray(weeklyGroups).slice(-12), // Last 12 weeks
                  month: toDataArray(monthlyGroups).slice(-12), // Last 12 months
                  year: toDataArray(yearlyGroups) // All years
                });

                console.log(`Dashboard: Loaded consumption data - Daily: ${dailyGroups.size}, Weekly: ${weeklyGroups.size}, Monthly: ${monthlyGroups.size}`);
              } else {
                console.log("No meter readings found for this apartment's meter");
              }
            } else {
              console.log("No smart meter found for apartment:", userData.apartment_id);
            }
          }
        }
      }

      if (!userId) return;

      // For demo user without session, use special handling
      const isDemoUserNoSession = !session && userId === "11111111-2222-3333-4444-555555555555";

      if (!session && !isDemoUserNoSession) return;

      // Fetch balance, last top-up, and last bill in parallel
      const [balanceResult, topupResult, billResult] = await Promise.all([
        supabase.from("user_balances").select("balance").eq("user_id", userId).maybeSingle(),
        supabase
          .from("user_topups")
          .select("completed_at, amount")
          .eq("user_id", userId)
          .eq("status", "completed")
          .order("completed_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from("bills")
          .select("amount, month, issue_date")
          .eq("user_id", userId)
          .order("issue_date", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      // Set balance
      if (balanceResult.data) {
        setBalance(Number(balanceResult.data.balance || 0));
      }

      // Set last top-up and check for recent recharge notification
      if (topupResult.data?.completed_at) {
        setLastTopUp(topupResult.data.completed_at);

        // Check if this is a recent top-up (within last 5 minutes) that we haven't shown yet
        const topupTime = new Date(topupResult.data.completed_at).getTime();
        const now = Date.now();
        const fiveMinutesAgo = now - 5 * 60 * 1000;
        const lastSeenTopup = localStorage.getItem(`lastSeenTopup_${userId}`);

        if (topupTime > fiveMinutesAgo && lastSeenTopup !== topupResult.data.completed_at) {
          const topupAmount = Number(topupResult.data.amount || 0);
          const topupKwh = ratePerKwh > 0 ? topupAmount / ratePerKwh : 0;

          setRechargeNotification({
            amount: topupAmount,
            kwh: topupKwh,
            timestamp: topupResult.data.completed_at
          });

          // Mark as seen after 30 seconds (auto-dismiss)
          setTimeout(() => {
            localStorage.setItem(`lastSeenTopup_${userId}`, topupResult.data.completed_at);
          }, 30000);
        }
      }

      // Set last bill
      if (billResult.data) {
        setLastBillAmount(Number(billResult.data.amount || 0));
        setLastBillMonth(billResult.data.month || "");
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  }, [user, ratePerKwh, viewingApartmentId]);

  useEffect(() => {
    if (user && !actingAsAdmin) {
      fetchUserData();
    }
  }, [user, actingAsAdmin, viewingApartmentId, fetchUserData]);

  // Subscribe to realtime updates on user_topups table for instant notifications
  useEffect(() => {
    if (!user?.id || actingAsAdmin) return;

    const channel = supabase
      .channel('dashboard-topup-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_topups',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          console.log('[Dashboard] Realtime topup update received:', payload);
          // Refresh data when a topup is inserted or updated
          fetchUserData();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, actingAsAdmin, fetchUserData]);

  // Subscribe to realtime updates on meter_readings for instant balance updates
  const [userMeterId, setUserMeterId] = useState<string | null>(null);

  // Get user's meter ID for realtime subscription
  useEffect(() => {
    const getUserMeter = async () => {
      if (!user?.id || actingAsAdmin) return;

      const apartmentId = viewingApartmentId ?? await getUserApartmentId(user.id);

      if (apartmentId) {
        const { data: meterData } = await supabase
          .from("smart_meters")
          .select("id")
          .eq("apartment_id", apartmentId)
          .maybeSingle();

        if (meterData?.id) {
          setUserMeterId(meterData.id);
        }
      }
    };

    getUserMeter();
  }, [user?.id, actingAsAdmin]);

  // Subscribe to meter readings changes
  useEffect(() => {
    if (!userMeterId || actingAsAdmin) return;

    const channel = supabase
      .channel('dashboard-meter-updates')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'meter_readings',
          filter: `meter_id=eq.${userMeterId}`,
        },
        (payload) => {
          console.log('[Dashboard] Realtime meter reading received:', payload);
          // Update balance directly from the new reading
          const newReading = payload.new as any;
          if (newReading?.electricity_balance_kwh) {
            const newBalance = Number(newReading.electricity_balance_kwh);
            console.log('[Dashboard] Updating balance to:', newBalance, 'kWh');
            setBalanceKwhFromMeter(newBalance);
            setBalance(newBalance * ratePerKwh);
          }
          // Also update overdraft from the same realtime reading
          const newOverdrawn = Number(newReading?.overdrawn_energy_kwh ?? 0);
          setOverdraftInfo(prev =>
            (newOverdrawn > 0 || (prev?.limit ?? 0) > 0)
              ? { overdrawn: newOverdrawn, limit: prev?.limit ?? 0 }
              : null
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userMeterId, actingAsAdmin, ratePerKwh]);

  const dismissRechargeNotification = () => {
    if (rechargeNotification && user?.id) {
      localStorage.setItem(`lastSeenTopup_${user.id}`, rechargeNotification.timestamp);
      setRechargeNotification(null);
    }
  };

  return (
    <MainLayout title="Dashboard">
      <div className="p-4 space-y-6 pb-20 max-w-full overflow-hidden">
        {/* Greeting */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 md:p-6 text-white">
          {viewingApartmentId && (
            <div className="flex items-center gap-1.5 text-xs bg-white/20 rounded-full px-3 py-1 w-fit mb-2">
              <Building2 className="h-3 w-3" />
              <span>Viewing as tenant</span>
            </div>
          )}
          <h2 className="text-lg md:text-xl font-semibold mb-1">Hello {userName || "User"}!</h2>
          {apartmentInfo ? (
            <p className="text-blue-100 text-sm md:text-base">
              Apt {apartmentInfo.number}{apartmentInfo.building ? ` · ${apartmentInfo.building}` : ''}
            </p>
          ) : (
            <p className="text-blue-100 text-sm md:text-base">Here's your energy consumption overview</p>
          )}
        </div>

        {/* Notice Board */}
        <NoticeBoard showCompact={!noticesExpanded} onToggle={() => setNoticesExpanded(!noticesExpanded)} />

        {/* Recharge Success Notification */}
        {rechargeNotification && (
          <Alert className="border-green-500 bg-green-50 dark:bg-green-900/20 relative">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800 dark:text-green-200 pr-8">
              Recharge completed! <strong>{rechargeNotification.kwh.toFixed(2)} kWh</strong> added for{" "}
              <strong>{formatTSh(rechargeNotification.amount)}</strong>
            </AlertDescription>
            <button
              onClick={dismissRechargeNotification}
              className="absolute right-2 top-2 p-1 rounded-full hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4 text-green-600" />
            </button>
          </Alert>
        )}

        {/* Low Balance Alert */}
        {isLowBalance && (
          <Alert variant="destructive" className="border-orange-500 bg-orange-50 dark:bg-orange-900/20">
            <AlertTriangle className="h-4 w-4 text-orange-600" />
            <AlertDescription className="text-orange-800 dark:text-orange-200">
              Low balance warning: You have less than 100 kWh remaining. Please top up soon to avoid service
              interruption.
            </AlertDescription>
          </Alert>
        )}

        {/* Account Balance */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-200 dark:border-green-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Account Balance</h3>
          <div className="flex items-baseline gap-3 mb-2">
            <p className="text-2xl md:text-3xl font-bold text-green-600">{formatTSh(balance)}</p>
            <span className="text-gray-400 dark:text-gray-500">|</span>
            <p className="text-xl md:text-2xl font-semibold text-green-600">{balanceKwh.toFixed(2)} kWh</p>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Rate: TZS {ratePerKwh}/kWh</p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Last top-up:{" "}
            {lastTopUp
              ? `${new Date(lastTopUp).toLocaleDateString()} ${new Date(lastTopUp).toLocaleTimeString()}`
              : "—"}
          </p>
          {overdraftInfo && (
            <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-700 flex flex-wrap gap-3">
              {overdraftInfo.overdrawn > 0 && (
                <div className="flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400 font-medium">
                  <ShieldAlert className="h-4 w-4" />
                  In overdraft: −{overdraftInfo.overdrawn.toFixed(2)} kWh
                </div>
              )}
              {overdraftInfo.limit > 0 && (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Overdraft limit: {overdraftInfo.limit.toFixed(2)} kWh
                </div>
              )}
            </div>
          )}
        </div>

        {/* Consumption Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
          <ConsumptionChart
            showControls={true}
            height="h-64 md:h-80"
            showPriceLabels={true}
            realData={consumptionByPeriod[chartPeriod].length > 0 ? consumptionByPeriod[chartPeriod] : undefined}
            pricePerKwh={ratePerKwh}
            externalPeriod={chartPeriod}
            onPeriodChange={(period) => setChartPeriod(period)}
          />
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Last Bill</h4>
            <p className="text-xl md:text-2xl font-bold text-blue-500 mb-1">
              {lastBillAmount > 0 ? formatTSh(lastBillAmount) : "—"}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{lastBillMonth || "—"}</p>
            <Button variant="outline" size="sm" className="w-full" onClick={() => navigate("/user/bills")}>
              <FileText className="w-4 h-4 mr-2" />
              See Bills
            </Button>
          </div>
        </div>

        {/* Tips */}
        <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900 dark:to-blue-900 rounded-xl p-4 border border-green-200 dark:border-green-700">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">💡 Daily Tip</h4>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Use energy-efficient LED bulbs to reduce your electricity consumption by up to 80%.
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default Dashboard;
